import {
  BookmarkPlus,
  ChevronDown,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import {
  useCatalogoFarmacosConsultaControllerListar,
  useCatalogoFarmacosConsultaControllerObter,
  useCatalogoFarmacosConsultaControllerProdutosDeMedicamento,
  useCatalogoFarmacosConsultaControllerSugerir,
} from '@/api/generated/catalogo-farmacos/catalogo-farmacos';
import type { TratamentoTemplateResponse } from '@/api/generated/model';
import {
  getTratamentoTemplatesControllerListarQueryKey,
  useTratamentoTemplatesControllerAtualizar,
  useTratamentoTemplatesControllerExcluir,
  useTratamentoTemplatesControllerListar,
} from '@/api/generated/tratamentos/tratamentos';
import { ApiError } from '@/api/http-client';
import { SalvarModeloDialogo } from '@/features/historico/components/SalvarModeloDialogo';
import { estruturaDoEditor, quantosEntramNoModelo } from '@/features/historico/modelosDeTratamento';
import {
  linhaVazia,
  type FormularioProntuario,
  type LinhaDeMedicamento,
} from '@/features/historico/prontuarioForm';
import {
  AVISO_NAO_PADRONIZADO,
  FREQUENCIAS,
  numeroParaCampo,
  paraNumero,
  ROTULO_FREQUENCIA,
  ROTULO_VIA,
  sugerirDosePorKg,
  VIAS,
} from '@/features/historico/tratamento';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { useDebounce } from '@/shared/hooks/useDebounce';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { ComboBox } from '@/shared/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';

/** O Radix não aceita `value=""` num `<SelectItem>`; a via "não informada" precisa de um. */
const VIA_NENHUMA = 'NENHUMA';

/**
 * Prefixo do `valor` das opções que vêm de **produto de parceiro**.
 *
 * ⚠️ É o que separa as duas fontes dentro de um `ComboBox` que só devolve uma
 * string. Sem ele, o id de um produto e o id de um fármaco são os dois uuids
 * indistinguíveis — e o `escolherFarmaco` gravaria um `produtoParceiroId` no
 * campo `catalogoFarmacoId`, apontando o tratamento para uma linha que não
 * existe no catálogo.
 *
 * `:` não aparece em uuid nem em nome de fármaco, então o texto livre nunca
 * colide com ele.
 */
const PREFIXO_DE_PRODUTO = 'produto:';

interface PropsDoEditor {
  form: UseFormReturn<FormularioProntuario>;
  /** Peso corrente do paciente — a SUGESTÃO de dose/kg sai dele. */
  pesoKg: number | null;
  /** Enquanto o tratamento vigente não chegou, o editor não convida a digitar. */
  carregandoVigente: boolean;
}

/**
 * O editor de tratamento que vive DENTRO do prontuário (Fase 1d, §3.4).
 *
 * ⚠️ Não é uma tela à parte. Schema estruturado que só se preenche numa tela
 * separada não é preenchido, e a coorte da Análise Comparativa nasce vazia — é
 * o risco que esta fase existe para evitar. Por isso ele compartilha o
 * `control` do formulário do prontuário: um `submit` só, uma validação só.
 */
export function EditorDeTratamento({ form, pesoKg, carregandoVigente }: PropsDoEditor) {
  const [menuDeModelos, setMenuDeModelos] = useState(false);
  const [salvandoModelo, setSalvandoModelo] = useState(false);
  /* P4: renomear e excluir vivem NO MENU, junto do que se aplica. */
  const [aRenomear, setARenomear] = useState<TratamentoTemplateResponse | null>(null);
  const [aExcluir, setAExcluir] = useState<TratamentoTemplateResponse | null>(null);

  /*
   * `watch`, e não `fields`: o `useFieldArray` só reflete a IDENTIDADE das
   * linhas, não o que foi digitado nelas. Ler de `fields` daria um modelo com
   * os campos vazios que a linha tinha quando foi acrescentada.
   */
  const linhasAtuais = form.watch('medicamentos') ?? [];
  const cabemNoModelo = quantosEntramNoModelo(linhasAtuais);
  const estruturaParaModelo = estruturaDoEditor(linhasAtuais);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'medicamentos',
  });

  // Só consulta quando o menu abre: a maioria dos atendimentos continua um
  // tratamento que já existe e nunca toca em modelo.
  const modelos = useTratamentoTemplatesControllerListar({
    // Continua habilitada enquanto um diálogo de renomear/excluir estiver
    // aberto: o menu já fechou (senão o foco brigaria com o diálogo), e sem
    // isto a lista seria descartada no meio da operação.
    query: { enabled: menuDeModelos || Boolean(aRenomear) || Boolean(aExcluir) },
  });

  const erroDaLista = form.formState.errors.medicamentos?.message;
  const erroDoTitulo = form.formState.errors.tituloTratamento?.message;

  /**
   * Aplicar modelo **ACRESCENTA**, nunca substitui.
   *
   * Trocar a lista pelo modelo apagaria o tratamento que o animal já faz — e
   * quem aplica um protocolo no meio de um acompanhamento está somando um
   * fármaco, não recomeçando do zero.
   */
  function aplicarModelo(indice: number) {
    const modelo = modelos.data?.itens[indice];
    if (!modelo?.estrutura) return;

    if (!form.getValues('tituloTratamento').trim() && modelo.tituloSugerido) {
      form.setValue('tituloTratamento', modelo.tituloSugerido, { shouldValidate: true });
    }

    for (const farmaco of modelo.estrutura.farmacos) {
      const linha: LinhaDeMedicamento = {
        ...linhaVazia(),
        catalogoFarmacoId: farmaco.catalogoFarmacoId ?? '',
        nomeLivre: farmaco.nomeLivre ?? '',
        nome: farmaco.nome,
        doseValor: numeroParaCampo(farmaco.doseSugerida),
        doseUnidade: farmaco.doseUnidade,
        dosePorKg: numeroParaCampo(farmaco.dosePorKgSugerida),
        frequencia: farmaco.frequencia,
        frequenciaLivre: farmaco.frequenciaLivre ?? '',
        via: farmaco.via ?? '',
      };
      append(linha);
    }

    setMenuDeModelos(false);
  }

  return (
    <div className="space-y-4 rounded-lg border border-brand/20 bg-surface-raised/50 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <Campo
          id="tituloTratamento"
          rotulo="Tratamento"
          erro={erroDoTitulo}
          dica='Como este protocolo se chama na ficha — ex.: "HAC — trilostano".'
          obrigatorio
          className="flex-1"
        >
          <Input
            id="tituloTratamento"
            className={CLASSE_CONTROLE}
            {...form.register('tituloTratamento')}
          />
        </Campo>

        <div className="flex gap-2 sm:mb-6">
          <DropdownMenu open={menuDeModelos} onOpenChange={setMenuDeModelos}>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline">
                Aplicar modelo
                <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-w-xs">
              {modelos.isPending ? (
                <DropdownMenuItem disabled>Carregando modelos...</DropdownMenuItem>
              ) : modelos.isError ? (
                /*
                 * ⚠️ Erro NÃO é lista vazia, e a checagem existe porque os dois
                 * caíam no mesmo ramo: `!modelos.data` era verdadeiro tanto para
                 * "nenhum modelo" quanto para uma requisição que falhou. Quem
                 * tomasse 403 aqui (permissão `tratamentos.visualizar` não
                 * semeada) leria "Nenhum modelo salvo" e passaria a tarde
                 * cadastrando um modelo que nunca apareceria.
                 */
                <DropdownMenuItem disabled>
                  {modelos.error?.erro?.mensagem ?? 'Não foi possível carregar seus modelos.'}
                </DropdownMenuItem>
              ) : !modelos.data || modelos.data.itens.length === 0 ? (
                <DropdownMenuItem disabled>
                  Nenhum modelo salvo ainda — monte o tratamento e use "Salvar como modelo".
                </DropdownMenuItem>
              ) : (
                modelos.data.itens.map((modelo, indice) => (
                  /*
                   * ⚠️ Renomear e excluir ficam AQUI, na mesma linha do modelo, e
                   * não numa tela de "gerenciar modelos". O momento em que o
                   * clínico descobre que um modelo está com o nome errado — ou que
                   * não serve mais — é justamente quando ele abre a lista para
                   * aplicar um. Mandá-lo a outra tela para consertar é garantir
                   * que ele não conserte.
                   */
                  <div key={modelo.id} className="flex items-center gap-0.5 pr-1">
                    <DropdownMenuItem
                      className="flex-1"
                      disabled={modelo.invalido || !modelo.estrutura}
                      onSelect={() => aplicarModelo(indice)}
                    >
                      {modelo.nome}
                      {modelo.invalido && ' (precisa ser refeito)'}
                    </DropdownMenuItem>

                    {/*
                    Fecha o menu ANTES de abrir o diálogo: os dois são camadas
                    do Radix com armadilha de foco própria, e abertos juntos o
                    campo do diálogo não recebe o cursor.
                  */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      aria-label={`Renomear ${modelo.nome}`}
                      onClick={() => {
                        setMenuDeModelos(false);
                        setARenomear(modelo);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-destructive"
                      aria-label={`Excluir ${modelo.nome}`}
                      onClick={() => {
                        setMenuDeModelos(false);
                        setAExcluir(modelo);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/*
            ⚠️ O botão fica AQUI, no editor, e não numa tela de "cadastrar
            modelo". Um modelo nasce de um tratamento montado para um paciente
            real — é ali que o clínico sabe quais fármacos, por que via e em que
            frequência. Tela separada obrigaria a redigitar tudo fora do
            contexto, e o resultado conhecido é o menu ficar vazio para sempre.
          */}
          <Button
            type="button"
            variant="outline"
            disabled={cabemNoModelo === 0}
            title={
              cabemNoModelo === 0
                ? 'Preencha ao menos um fármaco com unidade de dose para salvar como modelo.'
                : undefined
            }
            onClick={() => setSalvandoModelo(true)}
          >
            <BookmarkPlus className="mr-1 h-4 w-4" />
            Salvar como modelo
          </Button>
        </div>
      </div>

      {estruturaParaModelo && (
        <SalvarModeloDialogo
          aberto={salvandoModelo}
          aoFechar={() => setSalvandoModelo(false)}
          estrutura={estruturaParaModelo}
          tituloSugerido={form.watch('tituloTratamento') ?? ''}
          fora={linhasAtuais.length - cabemNoModelo}
        />
      )}

      {aRenomear && (
        <RenomearModeloDialogo modelo={aRenomear} aoFechar={() => setARenomear(null)} />
      )}

      {aExcluir && <ExcluirModeloDialogo modelo={aExcluir} aoFechar={() => setAExcluir(null)} />}

      {carregandoVigente && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Buscando o tratamento que valia nesta data...
        </p>
      )}

      {erroDaLista && <p className="text-xs text-destructive">{erroDaLista}</p>}

      <ul className="space-y-3">
        {fields.map((campo, indice) => (
          <li key={campo.id}>
            <LinhaDoMedicamento
              form={form}
              indice={indice}
              pesoKg={pesoKg}
              aoRemover={() => remove(indice)}
            />
          </li>
        ))}
      </ul>

      <Button type="button" variant="outline" size="sm" onClick={() => append(linhaVazia())}>
        <Plus className="h-4 w-4" />
        Acrescentar medicamento
      </Button>
    </div>
  );
}

interface PropsDaLinha {
  form: UseFormReturn<FormularioProntuario>;
  indice: number;
  pesoKg: number | null;
  aoRemover: () => void;
}

function LinhaDoMedicamento({ form, indice, pesoKg, aoRemover }: PropsDaLinha) {
  const [termo, setTermo] = useState('');
  const termoAdiado = useDebounce(termo);
  /** Editou a dose/kg à mão → a sugestão para de mexer nela. */
  const [dosePorKgManual, setDosePorKgManual] = useState(false);

  const erros = form.formState.errors.medicamentos?.[indice];

  const catalogoFarmacoId = form.watch(`medicamentos.${indice}.catalogoFarmacoId`);
  const nome = form.watch(`medicamentos.${indice}.nome`);
  const doseValor = form.watch(`medicamentos.${indice}.doseValor`);
  const frequencia = form.watch(`medicamentos.${indice}.frequencia`);
  const via = form.watch(`medicamentos.${indice}.via`);
  const apresentacaoId = form.watch(`medicamentos.${indice}.apresentacaoId`);
  const apresentacaoRotulo = form.watch(`medicamentos.${indice}.apresentacaoRotulo`);

  const catalogo = useCatalogoFarmacosConsultaControllerListar({
    porPagina: 20,
    ...(termoAdiado.trim() ? { busca: termoAdiado.trim() } : {}),
  });

  /*
   * P5 — A SEGUNDA FONTE: os produtos de parceiro da categoria Medicamento.
   *
   * ⚠️ Escolher um produto NÃO grava o produto. Cada item já vem do servidor
   * casado contra o catálogo global, e o que a linha grava é o
   * `catalogoFarmacoId` — o PRINCÍPIO ATIVO. Gravar o produto faria "Vetoryl da
   * clínica A" e "Vetoryl da clínica B" virarem duas coortes, e o filtro
   * "trilostano" deixaria de achar as duas. `produtoParceiroId` é vínculo
   * comercial, e o lugar dele é o receituário.
   */
  const produtos = useCatalogoFarmacosConsultaControllerProdutosDeMedicamento(
    termoAdiado.trim() ? { busca: termoAdiado.trim() } : {},
  );

  /* P6 — o fármaco digitado e não achado vira LINHA do catálogo, não texto. */
  const criarFarmaco = useCatalogoFarmacosConsultaControllerSugerir();
  const [avisoDeCatalogo, setAvisoDeCatalogo] = useState<string | null>(null);

  const detalhe = useCatalogoFarmacosConsultaControllerObter(catalogoFarmacoId, {
    query: { enabled: Boolean(catalogoFarmacoId) },
  });

  /**
   * ⚠️ A dose que veio do servidor NÃO é resugerida.
   *
   * A dose/kg é valor GRAVADO, não cálculo de leitura: o animal engordou entre
   * março e hoje, e recalcular ao abrir reescreveria a exposição de março com o
   * peso de agora — o diff acusaria um ajuste que ninguém fez. Por isso a
   * sugestão só entra depois que a dose muda NESTA sessão.
   */
  const doseAoAbrir = useRef(doseValor);

  useEffect(() => {
    if (doseValor === doseAoAbrir.current) return;
    if (dosePorKgManual) return;

    const sugerida = sugerirDosePorKg(paraNumero(doseValor), pesoKg);
    form.setValue(`medicamentos.${indice}.dosePorKg`, numeroParaCampo(sugerida));
  }, [doseValor, dosePorKgManual, pesoKg, form, indice]);

  /*
   * DUAS SEÇÕES, ROTULADAS NA PRÓPRIA LINHA.
   *
   * O catálogo vem primeiro porque é a fonte canônica; os produtos vêm depois,
   * cada um dizendo de que parceiro é e em que princípio ativo ele vai cair. O
   * rótulo entra no `detalhe` de cada opção, e não como cabeçalho de grupo,
   * porque o `ComboBox` é compartilhado (`shared/ui`) e não modela seção — a
   * alternativa seria uma opção falsa fazendo as vezes de título, que o teclado
   * percorreria como se fosse escolhível.
   *
   * O `detalhe` também é o que a busca local do `ComboBox` filtra: digitar
   * "Dechra" acha os produtos daquele parceiro.
   */
  const opcoesDeFarmaco = [
    ...(catalogo.data?.itens ?? []).map((farmaco) => ({
      valor: farmaco.id,
      rotulo: farmaco.nome,
      detalhe: farmaco.classeTerapeutica
        ? `Catálogo de fármacos · ${farmaco.classeTerapeutica}`
        : 'Catálogo de fármacos',
    })),
    ...(produtos.data?.itens ?? []).map((produto) => ({
      valor: `${PREFIXO_DE_PRODUTO}${produto.produtoParceiroId}`,
      rotulo: produto.nome,
      // ⚠️ A seta diz ANTES do clique o que vai ser gravado. Sem ela o
      // veterinário escolhe "Vetoryl 30 mg" e vê "Trilostano" aparecer no
      // campo, o que parece defeito — e a próxima coisa que ele faz é apagar.
      detalhe: produto.catalogoFarmacoNome
        ? `Seus produtos · ${produto.parceiroNome} → grava ${produto.catalogoFarmacoNome}`
        : `Seus produtos · ${produto.parceiroNome} — ainda sem princípio ativo no catálogo`,
    })),
  ];

  const opcoesDeApresentacao = (detalhe.data?.apresentacoes ?? [])
    .filter((apresentacao) => apresentacao.ativo)
    .map((apresentacao) => ({ valor: apresentacao.id, rotulo: apresentacao.rotulo }));

  /**
   * Grava a identidade do fármaco na linha.
   *
   * ⚠️ A apresentação é sempre zerada junto: ela pertence ao fármaco ANTERIOR, e
   * mantê-la faria a receita apontar para a marca de outro princípio ativo. Quem
   * tem apresentação para oferecer (o produto casado) a repõe logo em seguida.
   */
  function definirFarmaco(dados: {
    catalogoFarmacoId: string;
    nomeLivre: string;
    nome: string;
    unidadeDosePadrao?: string | null;
    apresentacaoId?: string;
    apresentacaoRotulo?: string;
  }) {
    form.setValue(`medicamentos.${indice}.catalogoFarmacoId`, dados.catalogoFarmacoId);
    form.setValue(`medicamentos.${indice}.nomeLivre`, dados.nomeLivre);
    form.setValue(`medicamentos.${indice}.nome`, dados.nome, { shouldValidate: true });

    form.setValue(`medicamentos.${indice}.apresentacaoId`, dados.apresentacaoId ?? '');
    form.setValue(`medicamentos.${indice}.nomeComercialLivre`, '');
    form.setValue(`medicamentos.${indice}.apresentacaoRotulo`, dados.apresentacaoRotulo ?? '');

    if (dados.unidadeDosePadrao && !form.getValues(`medicamentos.${indice}.doseUnidade`)) {
      form.setValue(`medicamentos.${indice}.doseUnidade`, dados.unidadeDosePadrao);
    }
  }

  /**
   * O QUE O CLÍNICO DIGITOU E NÃO ACHOU VIRA LINHA DO CATÁLOGO (P6).
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ **O `nomeLivre` é escrito PRIMEIRO, de propósito.** Se a rota falhar —
   *  rede caindo, servidor fora — o que o veterinário digitou continua na tela e
   *  o tratamento continua salvável, com o aviso antigo. Uma indisponibilidade
   *  do catálogo não pode virar "não consigo registrar o tratamento".
   *
   *  ⚠️ **`reaproveitado` muda a frase.** Quem digitou "trilostano" com
   *  "Trilostano" já cadastrado recebe a linha EXISTENTE — e dizer "fármaco
   *  criado" nesse caso ensinaria que cada digitação gera uma entrada nova.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  async function criarNoCatalogo(nomeDigitado: string) {
    const limpo = nomeDigitado.trim();

    definirFarmaco({ catalogoFarmacoId: '', nomeLivre: limpo, nome: limpo });
    setAvisoDeCatalogo(null);

    try {
      const criado = await criarFarmaco.mutateAsync({ data: { nome: limpo } });

      definirFarmaco({
        catalogoFarmacoId: criado.id,
        nomeLivre: '',
        nome: criado.nome,
        unidadeDosePadrao: criado.unidadeDosePadrao,
      });

      setAvisoDeCatalogo(
        criado.reaproveitado
          ? `"${criado.nome}" já existia no catálogo — usamos a entrada que já está lá, para o filtro continuar juntando os dois.`
          : `Fármaco novo: "${criado.nome}" entrou no catálogo, e a Análise Comparativa já consegue filtrar por ele. Um administrador vai revisar sinônimos e classe.`,
      );
    } catch (falha) {
      setAvisoDeCatalogo(null);
      // A do servidor, sempre — ela diz o que houve. A linha fica com
      // `nomeLivre`, que é o comportamento de antes, com o aviso de antes.
      toast.error(
        falha instanceof ApiError
          ? falha.message
          : 'Não foi possível cadastrar o fármaco no catálogo. Ele fica registrado como texto livre.',
      );
    }
  }

  function escolherFarmaco(valor: string) {
    setAvisoDeCatalogo(null);

    const doCatalogo = catalogo.data?.itens.find((item) => item.id === valor);
    if (doCatalogo) {
      definirFarmaco({
        catalogoFarmacoId: doCatalogo.id,
        nomeLivre: '',
        nome: doCatalogo.nome,
        unidadeDosePadrao: doCatalogo.unidadeDosePadrao,
      });
      return;
    }

    const produto = produtos.data?.itens.find(
      (item) => `${PREFIXO_DE_PRODUTO}${item.produtoParceiroId}` === valor,
    );

    if (produto) {
      // ⚠️ Casou: grava o PRINCÍPIO ATIVO, e o produto fica só como o caminho
      // que o veterinário usou para chegar nele. Quando o casamento também
      // resolveu a apresentação ("Vetoryl 30 mg"), ela vem junto — é ela que
      // transforma "1 comprimido" em dose.
      if (produto.catalogoFarmacoId) {
        definirFarmaco({
          catalogoFarmacoId: produto.catalogoFarmacoId,
          nomeLivre: '',
          nome: produto.catalogoFarmacoNome ?? produto.nome,
          ...(produto.apresentacaoId
            ? {
                apresentacaoId: produto.apresentacaoId,
                apresentacaoRotulo: produto.apresentacaoRotulo ?? '',
              }
            : {}),
        });
        return;
      }

      // Não casou: cai no fluxo do P6 com o NOME COMERCIAL. Fica torto — o
      // ideal é o princípio ativo — e é exatamente por isso que ele nasce
      // marcado como sugestão e vai para a fila do admin, que sabe fundir
      // "Vetoryl" em "Trilostano".
      void criarNoCatalogo(produto.nome);
      return;
    }

    void criarNoCatalogo(valor);
  }

  function escolherApresentacao(valor: string) {
    const doCatalogo = detalhe.data?.apresentacoes.find((item) => item.id === valor);

    form.setValue(`medicamentos.${indice}.apresentacaoId`, doCatalogo ? valor : '');
    form.setValue(`medicamentos.${indice}.nomeComercialLivre`, doCatalogo ? '' : valor);
    form.setValue(
      `medicamentos.${indice}.apresentacaoRotulo`,
      doCatalogo ? doCatalogo.rotulo : valor,
    );
  }

  const campoDosePorKg = form.register(`medicamentos.${indice}.dosePorKg`);
  const naoPadronizado = Boolean(nome) && !catalogoFarmacoId;

  return (
    <div className="space-y-3 rounded-md border border-brand/15 p-3">
      <div className="flex items-start gap-2">
        <Campo
          id={`farmaco-${indice}`}
          rotulo={`Nome do medicamento ${indice + 1}`}
          erro={erros?.nome?.message}
          className="flex-1"
        >
          <ComboBox
            id={`farmaco-${indice}`}
            valor={catalogoFarmacoId}
            rotuloSelecionado={nome}
            aoEscolher={escolherFarmaco}
            opcoes={opcoesDeFarmaco}
            aoBuscar={setTermo}
            carregando={catalogo.isFetching || produtos.isFetching || criarFarmaco.isPending}
            desabilitado={criarFarmaco.isPending}
            placeholder="Princípio ativo"
            placeholderBusca="Buscar no catálogo ou nos seus produtos..."
            permitirLivre
            /*
              ⚠️ O rótulo mudou de "(fora do catálogo)" para "cadastrar". A
              frase antiga descrevia um beco sem saída — o nome ia para
              `nomeLivre` e nunca entrava em filtro. Agora escolher isto CRIA a
              entrada, e o rótulo tem de dizer o que o clique faz.
            */
            rotuloLivre={(t) => `Cadastrar "${t}" no catálogo`}
            aria-label={`Nome do medicamento ${indice + 1}`}
          />
        </Campo>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="mt-8 shrink-0 text-destructive"
          onClick={aoRemover}
          aria-label={`Retirar ${nome || `fármaco ${indice + 1}`}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {criarFarmaco.isPending && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          Cadastrando no catálogo...
        </p>
      )}

      {/*
        ⚠️ O aviso NOVO. O antigo dizia "não entra em filtro por fármaco na
        Análise Comparativa" — verdadeiro, e um beco sem saída: a tela avisava do
        problema e não o resolvia. Agora o fármaco digitado VIRA linha do
        catálogo, filtrável na hora, e o texto diz isso em vez de lamentar.
      */}
      {avisoDeCatalogo && !criarFarmaco.isPending && (
        <p className="flex items-start gap-1.5 text-xs text-emerald-600">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {avisoDeCatalogo}
        </p>
      )}

      {/*
        ⚠️ Continua existindo, e continua correto: é o que sobra quando a
        criação FALHOU (rede, servidor fora) e a linha caiu de volta no
        `nomeLivre`. Nesse caso a frase antiga volta a ser exatamente verdadeira,
        e escondê-la faria o clínico salvar achando que está tudo padronizado.
      */}
      {naoPadronizado && !criarFarmaco.isPending && (
        <p className="flex items-start gap-1.5 text-xs text-amber-600">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {AVISO_NAO_PADRONIZADO}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Campo id={`dose-${indice}`} rotulo="Dose" erro={erros?.doseValor?.message} obrigatorio>
          <Input
            id={`dose-${indice}`}
            inputMode="decimal"
            className={CLASSE_CONTROLE}
            {...form.register(`medicamentos.${indice}.doseValor`)}
          />
        </Campo>

        <Campo
          id={`unidade-${indice}`}
          rotulo="Unidade"
          erro={erros?.doseUnidade?.message}
          obrigatorio
        >
          <Input
            id={`unidade-${indice}`}
            placeholder="mg"
            className={CLASSE_CONTROLE}
            {...form.register(`medicamentos.${indice}.doseUnidade`)}
          />
        </Campo>

        <Campo
          id={`dosekg-${indice}`}
          rotulo="Dose/kg"
          erro={erros?.dosePorKg?.message}
          dica={
            pesoKg
              ? `Sugerida a partir de ${pesoKg} kg. Edite se a dose/kg da época foi outra.`
              : 'Sem peso cadastrado — informe se quiser registrar.'
          }
        >
          <Input
            id={`dosekg-${indice}`}
            inputMode="decimal"
            className={CLASSE_CONTROLE}
            {...campoDosePorKg}
            onChange={(evento) => {
              setDosePorKgManual(true);
              void campoDosePorKg.onChange(evento);
            }}
          />
        </Campo>

        <Campo id={`freq-${indice}`} rotulo="Frequência" obrigatorio>
          <Select
            value={frequencia}
            onValueChange={(valor) =>
              form.setValue(`medicamentos.${indice}.frequencia`, valor, { shouldValidate: true })
            }
          >
            <SelectTrigger
              id={`freq-${indice}`}
              className={CLASSE_CONTROLE}
              aria-label={`Frequência do fármaco ${indice + 1}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCIAS.map((f) => (
                <SelectItem key={f} value={f}>
                  {ROTULO_FREQUENCIA[f] ?? f}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>
      </div>

      {frequencia === 'OUTRA' && (
        <Campo
          id={`freqlivre-${indice}`}
          rotulo="Qual frequência?"
          erro={erros?.frequenciaLivre?.message}
          obrigatorio
        >
          <Input
            id={`freqlivre-${indice}`}
            placeholder="Ex.: a cada 72 h"
            className={CLASSE_CONTROLE}
            {...form.register(`medicamentos.${indice}.frequenciaLivre`)}
          />
        </Campo>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Campo id={`via-${indice}`} rotulo="Via">
          <Select
            value={via || VIA_NENHUMA}
            onValueChange={(valor) =>
              form.setValue(`medicamentos.${indice}.via`, valor === VIA_NENHUMA ? '' : valor)
            }
          >
            <SelectTrigger
              id={`via-${indice}`}
              className={CLASSE_CONTROLE}
              aria-label={`Via do fármaco ${indice + 1}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={VIA_NENHUMA}>Não informada</SelectItem>
              {VIAS.map((v) => (
                <SelectItem key={v} value={v}>
                  {ROTULO_VIA[v] ?? v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        <Campo
          id={`apresentacao-${indice}`}
          rotulo="Apresentação"
          dica="A marca com a concentração. Fora do catálogo (manipulado), digite o nome."
        >
          <ComboBox
            id={`apresentacao-${indice}`}
            valor={apresentacaoId}
            rotuloSelecionado={apresentacaoRotulo}
            aoEscolher={escolherApresentacao}
            opcoes={opcoesDeApresentacao}
            carregando={detalhe.isFetching}
            placeholder={catalogoFarmacoId ? 'Selecione' : 'Escolha o fármaco antes'}
            placeholderBusca="Buscar apresentação..."
            vazio="Nenhuma apresentação cadastrada."
            permitirLivre
            rotuloLivre={(t) => `Usar "${t}" (manipulado)`}
            aria-label={`Apresentação do fármaco ${indice + 1}`}
          />
        </Campo>
      </div>
    </div>
  );
}

interface PropsDoModelo {
  modelo: TratamentoTemplateResponse;
  aoFechar: () => void;
}

/**
 * Renomear um modelo (P4).
 *
 * ⚠️ Só o NOME. `estrutura` não vai no corpo de propósito: o `PATCH` do backend
 * substitui a estrutura por inteiro quando ela vem, e mandar a que está na tela
 * transformaria "corrigir uma palavra no nome" em "sobrescrever o protocolo com
 * o que está aberto agora" — que não é o que ninguém espera de um lápis.
 */
function RenomearModeloDialogo({ modelo, aoFechar }: PropsDoModelo) {
  const [nome, setNome] = useState(modelo.nome);
  const [erro, setErro] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const renomear = useTratamentoTemplatesControllerAtualizar();

  async function salvar() {
    const limpo = nome.trim();
    if (!limpo) {
      setErro('Dê um nome ao modelo.');
      return;
    }
    if (limpo === modelo.nome) {
      aoFechar();
      return;
    }

    setErro(null);
    try {
      await renomear.mutateAsync({ id: modelo.id, data: { nome: limpo } });
      await queryClient.invalidateQueries({
        queryKey: getTratamentoTemplatesControllerListarQueryKey(),
      });
      toast.success(`Modelo renomeado para "${limpo}".`);
      aoFechar();
    } catch (falha) {
      // ⚠️ A do servidor, sempre — o 409 `TEMPLATE_DUPLICADO` diz que já existe
      // um modelo SEU com esse nome, e "Erro ao salvar" jogaria isso fora.
      setErro(falha instanceof ApiError ? falha.message : 'Não foi possível renomear o modelo.');
    }
  }

  return (
    <Dialog open onOpenChange={(estado) => !estado && aoFechar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Renomear modelo</DialogTitle>
          <DialogDescription>
            Muda só o nome na lista. Os fármacos, as vias e as frequências guardados continuam os
            mesmos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="novo-nome-do-modelo">Nome do modelo</Label>
          <Input
            id="novo-nome-do-modelo"
            value={nome}
            autoFocus
            maxLength={120}
            onChange={(evento) => setNome(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') {
                evento.preventDefault();
                void salvar();
              }
            }}
          />
        </div>

        {erro && (
          <p
            role="alert"
            className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-foreground"
          >
            {erro}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar} disabled={renomear.isPending}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void salvar()} disabled={renomear.isPending}>
            {renomear.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Excluir um modelo (P4).
 *
 * ⚠️ Aqui a exclusão é FÍSICA, e é seguro que seja: nenhum tratamento aponta
 * para o modelo — aplicar um COPIA a estrutura para dentro do editor. É o
 * oposto do catálogo de fármacos, onde "excluir" é desativar justamente porque
 * há histórico apontando para lá. Os tratamentos que nasceram deste modelo
 * continuam inteiros.
 */
function ExcluirModeloDialogo({ modelo, aoFechar }: PropsDoModelo) {
  const queryClient = useQueryClient();
  const excluir = useTratamentoTemplatesControllerExcluir();

  async function confirmar() {
    try {
      await excluir.mutateAsync({ id: modelo.id });
      await queryClient.invalidateQueries({
        queryKey: getTratamentoTemplatesControllerListarQueryKey(),
      });
      toast.success(`Modelo "${modelo.nome}" excluído.`);
    } catch (falha) {
      toast.error(falha instanceof ApiError ? falha.message : 'Não foi possível excluir o modelo.');
    } finally {
      aoFechar();
    }
  }

  return (
    <AlertDialog open onOpenChange={(estado) => !estado && aoFechar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir &quot;{modelo.nome}&quot;?</AlertDialogTitle>
          <AlertDialogDescription>
            O modelo some da sua lista. Os tratamentos que você já registrou a partir dele continuam
            intactos — aplicar um modelo copia a estrutura, não cria vínculo.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => void confirmar()}>Excluir</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
